import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, MessageSquare, Plane, Clock, Trash2, Plus, BarChart3, Edit, X, Check, RefreshCw, Send, Star, Info, Search, Upload, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CATEGORIES_MFR } from '../data/manufacturers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_PLACEHOLDERS = {
  jet: '/images/categorie/jet.jpg',
  turboelica: '/images/categorie/turboelica.png',
  elicotteri: '/images/categorie/elicotteri.png',
  ultraleggeri: '/images/categorie/ultraleggeri.png',
  'propulsione-singola': '/images/categorie/propulsione-singola.jpg',
  'propulsione-multipla': '/images/categorie/propulsione-multipla.jpg',
};

function formatPrice(p) {
  if (!p) return 'Su richiesta';
  return `€${Number(p).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;
}

function LocationAutocompleteEdit({ value, onChange }) {
  const inputRef = useRef(null);
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const timerRef = useRef(null);

  const search = (q) => {
    setQuery(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.length < 2) { setResults([]); setShowResults(false); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/places/autocomplete`, { params: { q } });
        setResults(res.data);
        setShowResults(true);
      } catch { setResults([]); }
    }, 300);
  };

  const select = (r) => {
    setQuery(r.description);
    setShowResults(false);
    onChange(r.description, r.lat || 0, r.lng || 0);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={e => search(e.target.value)}
        onFocus={() => results.length > 0 && setShowResults(true)}
        onBlur={() => setTimeout(() => setShowResults(false), 200)}
        placeholder="Cerca città..."
        className="flex h-9 w-full border border-zinc-700 bg-zinc-800 px-3 py-1 text-xs text-white placeholder-zinc-500 outline-none focus:border-[#C5A059]"
        data-testid="edit-location-autocomplete"
      />
      {showResults && results.length > 0 && (
        <div className="absolute z-[250] top-full mt-1 w-full bg-zinc-900 border border-zinc-700 max-h-40 overflow-auto">
          {results.map((r, i) => (
            <button key={i} type="button" onMouseDown={() => select(r)} className="block w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-800 hover:text-white border-b border-zinc-800 last:border-0">
              {r.description}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function sanitizeDescription(text) {
  // Block phones, emails, URLs — but ALLOW amounts (400.000, €400.000,00), years (2025), dates.
  let clean = text.replace(/\+\d[\d\s().\-]{6,}/g, '***');
  clean = clean.replace(/\b3\d{2}[\s.\-]?\d{3}[\s.\-]?\d{3,4}\b/g, '***');
  clean = clean.replace(/\b\d{10,}\b/g, '***');
  clean = clean.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '***');
  clean = clean.replace(/(https?:\/\/|www\.)[^\s]+/gi, '***');
  return clean;
}

function MfrSearch({ value, onChange, manufacturers }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const filtered = manufacturers.filter(m => !query || m.toLowerCase().includes(query.toLowerCase())).slice(0, 25);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input value={open ? query : value} onChange={e => { setQuery(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder="Cerca..." className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs pl-8" data-testid="edit-manufacturer" />
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 z-[300] bg-zinc-900 border border-zinc-700 mt-1 max-h-[200px] overflow-y-auto">
          {filtered.map(m => (
            <button key={m} onClick={() => { onChange(m); setQuery(''); setOpen(false); }} className={`w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-800 ${value === m ? 'text-[#C5A059]' : 'text-zinc-300'}`}>{m}</button>
          ))}
          {filtered.length === 0 && <p className="px-3 py-2 text-[10px] text-zinc-500">Nessun risultato</p>}
        </div>
      )}
    </div>
  );
}

function EditListingDialog({ listing, open, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [descWarning, setDescWarning] = useState('');
  const [editImages, setEditImages] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);

  useEffect(() => {
    if (listing) {
      setForm({
        manufacturer: listing.manufacturer || '',
        model: listing.model || '',
        variant: listing.variant || '',
        year: listing.year || 0,
        price: listing.price || 0,
        description: listing.description || '',
        location_city: listing.location_city || '',
        location_lat: listing.location_lat || 0,
        location_lng: listing.location_lng || 0,
        airport_code: listing.airport_code || '',
        advanced_ultralight: listing.advanced_ultralight || '',
        condition: listing.condition || 'used',
        last_inspection: listing.last_inspection || '',
        total_time: listing.total_time || 0,
        total_landings: listing.total_landings || 0,
        registration: listing.registration || '',
        serial_number: listing.serial_number || '',
        damage_history: listing.damage_history || '',
        hangar_kept: listing.hangar_kept || false,
        ifr_equipped: listing.ifr_equipped || false,
        ifr_certified: listing.ifr_certified || false,
        commercial_certified: listing.commercial_certified || false,
        jar_ops_certified: listing.jar_ops_certified || false,
        engine_type: listing.engine_type || '',
        engine_hours: listing.engine_hours || 0,
        max_speed: listing.max_speed || 0,
        range_km: listing.range_km || 0,
        seats: listing.seats || 0,
        avionics: listing.avionics || '',
        num_engines: listing.num_engines || '',
        propeller_type: listing.propeller_type || '',
        engine2_hours: listing.engine2_hours || 0,
        tbo_remaining: listing.tbo_remaining || 0,
        hot_section_completed: listing.hot_section_completed || false,
        total_cycles: listing.total_cycles || 0,
        apu_hours: listing.apu_hours || 0,
        interior_config: listing.interior_config || '',
        rotor_blades: listing.rotor_blades || 0,
        hoist_equipped: listing.hoist_equipped || false,
        ems_equipped: listing.ems_equipped || false,
        nvg_compatible: listing.nvg_compatible || false,
        mtow: listing.mtow || 0,
        useful_load: listing.useful_load || 0,
        contact_phone: listing.contact_phone || '',
        contact_email: listing.contact_email || '',
        status: listing.status || 'draft',
      });
      setDescWarning('');
      setEditImages((listing.images || []).map(p => ({ path: p, preview: `${API}/files/${p}` })));
    }
  }, [listing]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleDescriptionChange = (text) => {
    const hasPhone = /[\+]?[\d\s\-().]{7,}/.test(text);
    const hasEmail = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(text);
    const hasUrl = /(https?:\/\/|www\.)[^\s]+/i.test(text);
    if (hasPhone || hasEmail || hasUrl) {
      setDescWarning('Numeri di telefono, email e siti web non sono consentiti nella descrizione.');
    } else {
      setDescWarning('');
    }
    update('description', text);
  };

  const handleSave = async () => {
    setSaving(true);
    const data = { ...form, description: sanitizeDescription(form.description), images: editImages.map(img => img.path) };
    try {
      await axios.put(`${API}/listings/${listing.listing_id}`, data, { withCredentials: true });
      onSave();
      onClose();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleEditImageUpload = async (files) => {
    setUploadingImg(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API}/upload`, fd, { withCredentials: true });
        setEditImages(prev => [...prev, { path: res.data.path, preview: URL.createObjectURL(file) }]);
      } catch {}
    }
    setUploadingImg(false);
  };

  const removeEditImage = (idx) => setEditImages(prev => prev.filter((_, i) => i !== idx));

  const moveEditImage = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= editImages.length) return;
    setEditImages(prev => {
      const arr = [...prev];
      [arr[fromIdx], arr[toIdx]] = [arr[toIdx], arr[fromIdx]];
      return arr;
    });
  };

  if (!listing) return null;
  const cat = listing.category || '';
  const isUL = cat === 'ultraleggeri';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="edit-listing-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Modifica Annuncio
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Core Info */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Costruttore</Label>
              <MfrSearch value={form.manufacturer} onChange={v => update('manufacturer', v)} manufacturers={CATEGORIES_MFR[listing?.category] || []} />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Modello</Label>
              <Input value={form.model} onChange={e => update('model', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="edit-model" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Variante</Label>
              <Input value={form.variant} onChange={e => update('variant', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="edit-variant" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Anno</Label>
              <Input type="number" value={form.year} onChange={e => update('year', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="edit-year" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Prezzo (EUR)</Label>
              <Input type="number" value={form.price} onChange={e => update('price', parseFloat(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="edit-price" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Condizioni</Label>
              <Select value={form.condition} onValueChange={v => update('condition', v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-9 text-xs" data-testid="edit-condition"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 z-[200]">
                  <SelectItem value="new" className="text-zinc-300">Nuovo</SelectItem>
                  <SelectItem value="used" className="text-zinc-300">Usato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location */}
          <div className={`grid gap-3 ${isUL ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Posizione</Label>
              <LocationAutocompleteEdit
                value={form.location_city}
                onChange={(city, lat, lng) => {
                  update('location_city', city);
                  if (lat) update('location_lat', lat);
                  if (lng) update('location_lng', lng);
                }}
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Codice Aeroporto</Label>
              <Input value={form.airport_code} onChange={e => update('airport_code', e.target.value.toUpperCase())} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" data-testid="edit-airport" />
            </div>
            {isUL && (
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">UL Avanzato</Label>
                <Select value={form.advanced_ultralight || 'none'} onValueChange={v => update('advanced_ultralight', v === 'none' ? '' : v)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-9 text-xs"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 z-[200]">
                    <SelectItem value="none" className="text-zinc-300">-</SelectItem>
                    <SelectItem value="si" className="text-zinc-300">Si</SelectItem>
                    <SelectItem value="no" className="text-zinc-300">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Hours & Inspection */}
          <div className={`grid gap-3 ${!isUL ? 'grid-cols-5' : 'grid-cols-4'}`}>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Ore Airframe</Label>
              <Input type="number" value={form.total_time} onChange={e => update('total_time', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" data-testid="edit-total-time" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Atterraggi</Label>
              <Input type="number" value={form.total_landings} onChange={e => update('total_landings', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" data-testid="edit-landings" />
            </div>
            {!isUL && (
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">Posti (PIC+PAX)</Label>
                <Input type="number" value={form.seats} onChange={e => update('seats', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="edit-seats" />
              </div>
            )}
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Ultima Ispezione Annuale</Label>
              <Input type="date" value={form.last_inspection || ''} onChange={e => update('last_inspection', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="edit-inspection" />
            </div>
          </div>

          {/* Registration & Serial */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Registrazione</Label>
              <Input value={form.registration} onChange={e => update('registration', e.target.value.toUpperCase())} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" data-testid="edit-registration" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">N. Serie</Label>
              <Input value={form.serial_number} onChange={e => update('serial_number', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" data-testid="edit-serial" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Storico Danni</Label>
              <Select value={form.damage_history || 'none'} onValueChange={v => update('damage_history', v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-9 text-xs"><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 z-[200]">
                  <SelectItem value="none" className="text-zinc-300">-</SelectItem>
                  <SelectItem value="never" className="text-zinc-300">Mai danneggiato</SelectItem>
                  <SelectItem value="yes" className="text-zinc-300">Danneggiato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Technical */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Tipo Motore</Label>
              <Input value={form.engine_type} onChange={e => update('engine_type', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="edit-engine-type" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Ore Motore</Label>
              <Input type="number" value={form.engine_hours} onChange={e => update('engine_hours', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" data-testid="edit-engine-hours" />
            </div>
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Avionica</Label>
              <Input value={form.avionics} onChange={e => update('avionics', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" data-testid="edit-avionics" />
            </div>
          </div>

          {/* Category-specific fields in edit */}
          {(cat === 'propulsione-multipla' || cat === 'turboelica' || cat === 'jet') && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">N. Motori</Label>
                <Input type="number" value={form.num_engines} onChange={e => update('num_engines', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
              </div>
              {cat === 'propulsione-multipla' && (
                <div>
                  <Label className="text-zinc-400 text-[10px] mb-1 block">Ore Motore 2</Label>
                  <Input type="number" value={form.engine2_hours} onChange={e => update('engine2_hours', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
                </div>
              )}
              {(cat === 'propulsione-multipla' || cat === 'turboelica') && (
                <div>
                  <Label className="text-zinc-400 text-[10px] mb-1 block">Tipo Elica</Label>
                  <Input value={form.propeller_type} onChange={e => update('propeller_type', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" />
                </div>
              )}
              {(cat === 'turboelica' || cat === 'elicotteri') && (
                <div>
                  <Label className="text-zinc-400 text-[10px] mb-1 block">TBO Rimanenti</Label>
                  <Input type="number" value={form.tbo_remaining} onChange={e => update('tbo_remaining', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
                </div>
              )}
              {cat === 'jet' && (
                <>
                  <div>
                    <Label className="text-zinc-400 text-[10px] mb-1 block">Cicli Totali</Label>
                    <Input type="number" value={form.total_cycles} onChange={e => update('total_cycles', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-[10px] mb-1 block">Ore APU</Label>
                    <Input type="number" value={form.apu_hours} onChange={e => update('apu_hours', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
                  </div>
                </>
              )}
            </div>
          )}

          {cat === 'jet' && (
            <div>
              <Label className="text-zinc-400 text-[10px] mb-1 block">Config. Interni</Label>
              <Input value={form.interior_config} onChange={e => update('interior_config', e.target.value)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs" />
            </div>
          )}

          {cat === 'elicotteri' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">Pale Rotore</Label>
                <Input type="number" value={form.rotor_blades} onChange={e => update('rotor_blades', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
              </div>
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">TBO Rimanenti</Label>
                <Input type="number" value={form.tbo_remaining} onChange={e => update('tbo_remaining', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
              </div>
            </div>
          )}

          {!isUL && (
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">Vel. Max</Label>
                <Input type="number" value={form.max_speed} onChange={e => update('max_speed', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
              </div>
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">Autonomia</Label>
                <Input type="number" value={form.range_km} onChange={e => update('range_km', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
              </div>
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">MTOW (kg)</Label>
                <Input type="number" value={form.mtow} onChange={e => update('mtow', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
              </div>
              <div>
                <Label className="text-zinc-400 text-[10px] mb-1 block">Carico Utile</Label>
                <Input type="number" value={form.useful_load} onChange={e => update('useful_load', parseInt(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700 text-white h-9 text-xs font-mono" />
              </div>
            </div>
          )}

          {/* Characteristics */}
          <div>
            <Label className="text-zinc-400 text-[10px] mb-2 block">Caratteristiche</Label>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <label className="flex items-center gap-2 cursor-pointer group" data-testid="edit-hangar">
                <div className={`w-4 h-4 border flex items-center justify-center transition-colors ${form.hangar_kept ? 'bg-[#C5A059] border-[#C5A059]' : 'border-zinc-700 bg-zinc-800 group-hover:border-zinc-500'}`}>
                  {form.hangar_kept && <Check size={10} className="text-black" />}
                </div>
                <input type="checkbox" checked={form.hangar_kept || false} onChange={e => update('hangar_kept', e.target.checked)} className="hidden" />
                <span className="text-[11px] text-zinc-300">Tenuto in hangar</span>
              </label>
              {!isUL && [
                { key: 'ifr_equipped', label: 'Attrezzato IFR' },
                { key: 'ifr_certified', label: 'Certificato IFR' },
                { key: 'commercial_certified', label: 'Uso Commerciale' },
                { key: 'jar_ops_certified', label: 'JAR-OPS 1' },
              ].map(ch => (
                <label key={ch.key} className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-4 h-4 border flex items-center justify-center transition-colors ${form[ch.key] ? 'bg-[#C5A059] border-[#C5A059]' : 'border-zinc-700 bg-zinc-800 group-hover:border-zinc-500'}`}>
                    {form[ch.key] && <Check size={10} className="text-black" />}
                  </div>
                  <input type="checkbox" checked={form[ch.key] || false} onChange={e => update(ch.key, e.target.checked)} className="hidden" />
                  <span className="text-[11px] text-zinc-400">{ch.label}</span>
                </label>
              ))}
              {cat === 'elicotteri' && [
                { key: 'hoist_equipped', label: 'Verricello' },
                { key: 'ems_equipped', label: 'EMS/HEMS' },
                { key: 'nvg_compatible', label: 'NVG' },
              ].map(ch => (
                <label key={ch.key} className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-4 h-4 border flex items-center justify-center transition-colors ${form[ch.key] ? 'bg-[#C5A059] border-[#C5A059]' : 'border-zinc-700 bg-zinc-800 group-hover:border-zinc-500'}`}>
                    {form[ch.key] && <Check size={10} className="text-black" />}
                  </div>
                  <input type="checkbox" checked={form[ch.key] || false} onChange={e => update(ch.key, e.target.checked)} className="hidden" />
                  <span className="text-[11px] text-zinc-400">{ch.label}</span>
                </label>
              ))}
              {cat === 'turboelica' && (
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-4 h-4 border flex items-center justify-center transition-colors ${form.hot_section_completed ? 'bg-[#C5A059] border-[#C5A059]' : 'border-zinc-700 bg-zinc-800 group-hover:border-zinc-500'}`}>
                    {form.hot_section_completed && <Check size={10} className="text-black" />}
                  </div>
                  <input type="checkbox" checked={form.hot_section_completed || false} onChange={e => update('hot_section_completed', e.target.checked)} className="hidden" />
                  <span className="text-[11px] text-zinc-400">Hot Section</span>
                </label>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-zinc-400 text-[10px] mb-1 block">Descrizione</Label>
            <Textarea value={form.description} onChange={e => handleDescriptionChange(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white min-h-[220px] text-sm leading-relaxed" data-testid="edit-description" />
            {descWarning && <p className="text-[10px] text-amber-400 mt-1">{descWarning}</p>}
          </div>

          {/* Images */}
          <div>
            <Label className="text-zinc-400 text-[10px] mb-1 block">Immagini ({editImages.length}/10)</Label>
            <div className="grid grid-cols-4 gap-2">
              {editImages.map((img, i) => (
                <div
                  key={`eimg-${i}`}
                  className={`relative aspect-[4/3] bg-zinc-800 overflow-hidden border ${i === 0 ? 'border-[#C5A059]/50' : 'border-zinc-700'}`}
                  draggable
                  onDragStart={e => { e.dataTransfer.setData('text/plain', i.toString()); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const from = parseInt(e.dataTransfer.getData('text/plain')); if (from !== i) moveEditImage(from, i); }}
                >
                  <img src={img.preview} alt="" className="w-full h-full object-cover" />
                  {i === 0 && <span className="absolute top-1 left-1 bg-[#C5A059] text-black text-[8px] font-bold px-1.5 py-0.5">COPERTINA</span>}
                  <button onClick={() => removeEditImage(i)} className="absolute top-1 right-1 w-5 h-5 bg-zinc-950/80 flex items-center justify-center text-white hover:bg-red-500/80 transition-colors text-[10px]">
                    <X size={10} />
                  </button>
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                    {i > 0 && <button onClick={() => moveEditImage(i, i - 1)} className="w-5 h-5 bg-zinc-950/80 flex items-center justify-center text-white hover:bg-zinc-700"><ChevronLeft size={10} /></button>}
                    {i < editImages.length - 1 && <button onClick={() => moveEditImage(i, i + 1)} className="w-5 h-5 bg-zinc-950/80 flex items-center justify-center text-white hover:bg-zinc-700"><ChevronRight size={10} /></button>}
                  </div>
                </div>
              ))}
              {editImages.length < 10 && (
                <label className="aspect-[4/3] border border-dashed border-zinc-700 flex flex-col items-center justify-center cursor-pointer hover:border-[#C5A059]/50 transition-colors">
                  <input type="file" multiple accept="image/*" onChange={e => handleEditImageUpload(Array.from(e.target.files))} className="hidden" />
                  {uploadingImg ? <Loader2 size={16} className="animate-spin text-[#C5A059]" /> : <Upload size={16} className="text-zinc-500" />}
                  <span className="text-[9px] text-zinc-600 mt-1">Aggiungi</span>
                </label>
              )}
            </div>
          </div>

          {/* Status */}
          <div>
            <Label className="text-zinc-400 text-[10px] mb-1 block">Stato</Label>
            <Select value={form.status} onValueChange={v => update('status', v)}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-9 text-xs" data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 z-[200]">
                <SelectItem value="published" className="text-zinc-300">Pubblicato</SelectItem>
                <SelectItem value="expired" className="text-zinc-300">Scaduto</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-5 py-2.5 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors" data-testid="edit-cancel-btn">
              Annulla
            </button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2.5 text-sm font-medium hover:bg-[#D4B271] transition-colors disabled:opacity-50" data-testid="edit-save-btn">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              Salva Modifiche
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editListing, setEditListing] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [featureOpen, setFeatureOpen] = useState(false);
  const [featureTarget, setFeatureTarget] = useState(null);
  const [featureDuration, setFeatureDuration] = useState('featured_30d');
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [convoMessages, setConvoMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const chatEndRef = useRef(null);
  const chatScrollAnchor = useRef(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab') || 'active';
    return t === 'drafts' ? 'active' : t;
  });

  // React to URL searchParams changes (e.g. from notification click)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const fetchData = () => {
    if (!user) return;
    const listingParam = searchParams.get('listing');
    axios.get(`${API}/dashboard/stats`, { withCredentials: true }).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/listings/my`, { withCredentials: true }).then(r => setListings(r.data)).catch(() => {});
    axios.get(`${API}/messages/conversations`, { withCredentials: true }).then(r => {
      setConversations(r.data);
      // Auto-open conversation from notification
      if (listingParam && r.data.length > 0) {
        const targetConvo = r.data.find(c => c.listing?.listing_id === listingParam);
        if (targetConvo) {
          setActiveTab('messages');
          // Use openConvo to also fetch messages
          setSelectedConvo(targetConvo);
          axios.get(`${API}/messages/${targetConvo.listing_id}`, { withCredentials: true })
            .then(mr => {
              setConvoMessages(mr.data);
              setTimeout(() => chatScrollAnchor.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            })
            .catch(() => setConvoMessages([]));
        }
      }
    }).catch(() => {});
  };

  useEffect(() => { fetchData(); }, [user, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteListing = async (id) => {
    if (!window.confirm('Eliminare questo annuncio?')) return;
    try {
      await axios.delete(`${API}/listings/${id}`, { withCredentials: true });
      setListings(prev => prev.filter(l => l.listing_id !== id));
    } catch { /* ignore */ }
  };

  const openEdit = (listing) => {
    setEditListing(listing);
    setEditOpen(true);
  };

  const republishListing = async (listing) => {
    try {
      await axios.put(`${API}/listings/${listing.listing_id}`, { status: 'published' }, { withCredentials: true });
      fetchData();
    } catch { /* ignore */ }
  };

  const isAdminUser = (user?.email || '').toLowerCase() === 'info@aerodeals.it' || user?.role === 'admin';

  const confirmFeature = async () => {
    if (!featureTarget) return;
    try {
      // Admin user (info@aerodeals.it) activates Featured for free — no Stripe
      if (isAdminUser) {
        await axios.post(
          `${API}/listings/${featureTarget.listing_id}/feature`,
          {},
          { withCredentials: true }
        );
        setFeatureOpen(false);
        setFeatureTarget(null);
        fetchData();
        return;
      }
      const { data } = await axios.post(
        `${API}/checkout/session`,
        {
          package_id: featureDuration,
          origin_url: window.location.origin,
          listing_id: featureTarget.listing_id,
        },
        { withCredentials: true }
      );
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      alert('Impossibile avviare il pagamento. Riprova.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore pagamento');
    }
  };

  const isListingFeatured = (l) => l.featured && l.featured_until && new Date(l.featured_until) > new Date();

  const openConvo = async (convo) => {
    setSelectedConvo(convo);
    try {
      const res = await axios.get(`${API}/messages/${convo.listing_id}`, { withCredentials: true });
      setConvoMessages(res.data);
      setTimeout(() => chatScrollAnchor.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { setConvoMessages([]); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedConvo) return;
    try {
      await axios.post(`${API}/messages`, {
        listing_id: selectedConvo.listing_id,
        receiver_id: selectedConvo.other_user.user_id,
        content: replyText,
      }, { withCredentials: true });
      setReplyText('');
      openConvo(selectedConvo);
    } catch {}
  };

  const deleteConversation = async (convo, e) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questa conversazione?')) return;
    try {
      await axios.delete(`${API}/messages/conversation/${convo.listing_id}/${convo.other_user.user_id}`, { withCredentials: true });
      setConversations(prev => prev.filter(c => c !== convo));
      if (selectedConvo === convo) setSelectedConvo(null);
    } catch {}
  };

  const getListingImage = (listing) => {
    if (listing?.images?.length > 0) return `${API}/files/${listing.images[0]}`;
    return CATEGORY_PLACEHOLDERS[listing?.category] || CATEGORY_PLACEHOLDERS.jet;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">Accedi per visualizzare la dashboard</p>
          <Link to="/login" className="text-[#C5A059] hover:underline">Accedi</Link>
        </div>
      </div>
    );
  }

  const activeListings = listings.filter(l => l.status === 'published');
  const expiredListings = listings.filter(l => l.status === 'expired');

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-2">Dashboard</p>
            <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Ciao, {user.name}
            </h1>
          </div>
          <Link to="/create-listing" className="flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2.5 text-sm font-medium hover:bg-[#D4B271] transition-colors" data-testid="dashboard-create-listing-btn">
            <Plus size={16} /> Nuovo Annuncio
          </Link>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-testid="dashboard-stats">
            <div className="bg-zinc-900 border border-zinc-800 p-5">
              <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2"><Plane size={14} /> Attivi</div>
              <p className="text-2xl font-bold text-white font-mono">{stats.active_listings}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-5">
              <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2"><Eye size={14} /> Visualizzazioni</div>
              <p className="text-2xl font-bold text-white font-mono">{stats.total_views}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-5">
              <div className="flex items-center gap-2 text-zinc-500 text-xs mb-2"><MessageSquare size={14} /> Richieste</div>
              <p className="text-2xl font-bold text-white font-mono">{stats.total_inquiries}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1 flex-wrap">
            <TabsTrigger value="active" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-500 text-xs sm:text-sm" data-testid="tab-active">
              Attivi ({activeListings.length})
            </TabsTrigger>
            <TabsTrigger value="expired" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-500 text-xs sm:text-sm" data-testid="tab-expired">
              Scaduti ({expiredListings.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-500 text-xs sm:text-sm" data-testid="tab-messages">
              Messaggi ({conversations.length})
            </TabsTrigger>
          </TabsList>

          {/* Listing tabs (active, expired) */}
          {['active', 'expired'].map(tabKey => {
            const items = tabKey === 'active' ? activeListings : expiredListings;
            return (
              <TabsContent key={tabKey} value={tabKey}>
                {items.length > 0 ? (
                  <div className="space-y-3">
                    {items.map(l => (
                      <div key={l.listing_id} className={`bg-zinc-900 border p-4 flex items-center justify-between gap-4 ${isListingFeatured(l) ? 'border-[#C5A059]/30' : 'border-zinc-800'}`} data-testid={`listing-row-${l.listing_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {isListingFeatured(l) && (
                              <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-[#C5A059]/15 text-[#C5A059] px-1.5 py-0.5" data-testid={`featured-label-${l.listing_id}`}>
                                <Star size={9} className="fill-[#C5A059]" /> In Evidenza fino al {new Date(l.featured_until).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </span>
                            )}
                            <span className="text-[10px] font-mono uppercase tracking-wider text-[#C5A059]">{l.category?.replace('-', ' ')}</span>
                            {l.status === 'published' ? (
                              <span className="text-[10px] font-mono uppercase tracking-wider bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 border border-emerald-500/30">Pubblicato</span>
                            ) : l.status === 'expired' ? (
                              <span className="text-[10px] font-mono uppercase tracking-wider bg-red-500/15 text-red-400 px-1.5 py-0.5 border border-red-500/30">Scaduto</span>
                            ) : (
                              <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">{l.status}</Badge>
                            )}
                            {l.expires_at && (
                              <span className="text-[10px] font-mono text-zinc-500">
                                {l.status === 'expired' ? 'Scaduto il ' : 'Scade il '}
                                {new Date(l.expires_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                          </div>
                          <h3 className="text-sm font-medium text-white leading-snug break-words" data-testid={`listing-title-${l.listing_id}`}>{l.manufacturer} {l.model}{l.variant ? ` ${l.variant}` : ''}{l.registration ? ` · ${l.registration}` : ''}</h3>
                          <div className="flex items-center gap-4 mt-1 text-xs text-zinc-500">
                            <span>{formatPrice(l.price)}</span>
                            <span className="flex items-center gap-1"><Eye size={10} /> {l.views}</span>
                            <span className="flex items-center gap-1"><MessageSquare size={10} /> {l.inquiries}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link to={l.slug ? `/listings/${l.slug}` : `/listings/${l.listing_id}`} className="w-8 h-8 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors" data-testid={`view-listing-${l.listing_id}`}><Eye size={14} /></Link>
                          <button onClick={() => openEdit(l)} className="w-8 h-8 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-[#C5A059] hover:border-[#C5A059]/50 transition-colors" data-testid={`edit-listing-${l.listing_id}`}><Edit size={14} /></button>
                          {tabKey === 'active' && !isListingFeatured(l) && (
                            <button onClick={() => { setFeatureTarget(l); setFeatureDuration('featured_30d'); setFeatureOpen(true); }} className="w-8 h-8 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-400/50 transition-colors" title="Metti in evidenza" data-testid={`feature-listing-${l.listing_id}`}><Star size={14} /></button>
                          )}
                          {tabKey === 'active' && isListingFeatured(l) && (
                            <div className="w-8 h-8 flex items-center justify-center border border-[#C5A059]/30 text-[#C5A059]" title="Gia in evidenza"><Star size={14} className="fill-[#C5A059]" /></div>
                          )}
                          {tabKey === 'expired' && (
                            <button onClick={() => republishListing(l)} className="inline-flex items-center gap-1.5 px-3 h-8 border border-emerald-500/40 text-emerald-400 text-xs font-medium hover:bg-emerald-500/10 transition-colors" data-testid={`republish-listing-${l.listing_id}`}>
                              <RefreshCw size={12} /> Rinnova ora
                            </button>
                          )}
                          <button onClick={() => deleteListing(l.listing_id)} className="w-8 h-8 flex items-center justify-center border border-zinc-700 text-zinc-400 hover:text-red-400 hover:border-red-400/50 transition-colors" data-testid={`delete-listing-${l.listing_id}`}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <p className="text-sm">Nessun annuncio</p>
                    {tabKey === 'active' && <Link to="/create-listing" className="text-[#C5A059] text-xs mt-2 inline-block hover:underline">Pubblica il primo annuncio</Link>}
                  </div>
                )}
              </TabsContent>
            );
          })}

          {/* Messages Tab - Conversations */}
          <TabsContent value="messages">
            {conversations.length > 0 ? (
              <div className="space-y-3">
                {conversations.map((c, i) => (
                  <div key={i} className={`bg-zinc-900 border transition-colors flex items-center gap-4 ${c.unread_count > 0 ? 'border-[#C5A059]/40' : 'border-zinc-800 hover:border-zinc-600'}`} data-testid={`convo-${i}`}>
                    <button onClick={() => openConvo(c)} className="flex-1 flex items-center gap-4 p-4 text-left min-w-0">
                      <div className="w-14 h-12 bg-zinc-800 overflow-hidden shrink-0 relative">
                        <img src={getListingImage(c.listing)} alt="" className="w-full h-full object-cover" />
                        {c.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C5A059] text-black text-[9px] font-bold rounded-full flex items-center justify-center">{c.unread_count}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-medium truncate ${c.unread_count > 0 ? 'text-[#C5A059]' : 'text-white'}`}>{c.other_user?.name || 'Utente'}</span>
                          <span className="text-[10px] text-zinc-600 shrink-0 ml-2">{new Date(c.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</span>
                        </div>
                        <p className="text-xs text-zinc-500 truncate">{c.listing?.manufacturer} {c.listing?.model} - {c.listing?.category?.replace('-',' ')}</p>
                        <p className={`text-xs mt-1 truncate ${c.unread_count > 0 ? 'text-white font-medium' : 'text-zinc-400'}`}>{c.content}</p>
                      </div>
                    </button>
                    <button onClick={(e) => deleteConversation(c, e)} className="w-8 h-8 mr-3 flex items-center justify-center border border-zinc-700 text-zinc-500 hover:text-red-400 hover:border-red-400/50 transition-colors shrink-0" data-testid={`delete-convo-${i}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-500"><p className="text-sm">Nessun messaggio</p></div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Chat Popup Dialog */}
      <Dialog open={!!selectedConvo} onOpenChange={(open) => { if (!open) setSelectedConvo(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-xl max-h-[85vh] flex flex-col p-0 gap-0" data-testid="chat-dialog">
          {selectedConvo && (
            <>
              {/* Chat Header with listing info */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-800 shrink-0">
                <div className="w-12 h-10 bg-zinc-800 overflow-hidden shrink-0">
                  <img src={getListingImage(selectedConvo.listing)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{selectedConvo.listing?.manufacturer} {selectedConvo.listing?.model}</p>
                  <p className="text-[10px] text-zinc-500">{selectedConvo.listing?.category?.replace('-',' ')} &middot; {selectedConvo.other_user?.name || 'Utente'}</p>
                </div>
                <Link
                  to={selectedConvo.listing?.slug ? `/listings/${selectedConvo.listing.slug}` : `/listings/${selectedConvo.listing?.listing_id}`}
                  onClick={() => setSelectedConvo(null)}
                  className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[#C5A059] border border-[#C5A059]/30 px-2.5 py-1.5 hover:bg-[#C5A059]/10 transition-colors shrink-0"
                  data-testid="chat-go-to-listing"
                >
                  <Eye size={11} /> Annuncio
                </Link>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-[250px] max-h-[50vh]" ref={chatEndRef}>
                {convoMessages.map(m => (
                  <div key={m.message_id} className={`p-3 relative ${m.sender_id === user.user_id ? 'bg-[#C5A059]/10 border border-[#C5A059]/20 ml-12' : 'bg-zinc-800/50 border border-zinc-700/50 mr-12'}`}>
                    {!m.read && m.sender_id !== user.user_id && (
                      <span className="absolute top-2 right-2 w-2 h-2 bg-[#C5A059] rounded-full" />
                    )}
                    <div className="flex justify-between mb-1">
                      <span className="text-[10px] font-medium text-zinc-400">{m.sender_name}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-zinc-600">{new Date(m.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        {m.sender_id === user.user_id && (
                          <span className={`text-[10px] ${m.read ? 'text-[#C5A059]' : 'text-zinc-600'}`} title={m.read ? 'Letto' : 'Inviato'}>
                            {m.read ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-zinc-300">{m.content}</p>
                  </div>
                ))}
                <div ref={chatScrollAnchor} />
              </div>

              {/* Reply Input */}
              <div className="flex items-end gap-2 px-5 py-4 border-t border-zinc-800 shrink-0">
                <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }}} placeholder="Scrivi un messaggio..." className="bg-zinc-800 border-zinc-700 text-white text-sm flex-1 min-h-[44px] max-h-[100px] resize-none" data-testid="reply-textarea" />
                <button onClick={sendReply} className="bg-[#C5A059] text-black w-10 h-10 flex items-center justify-center hover:bg-[#D4B271] transition-colors shrink-0" data-testid="reply-send-btn">
                  <Send size={16} />
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Feature Listing Dialog */}
      <Dialog open={featureOpen} onOpenChange={setFeatureOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md" data-testid="feature-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg text-white flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <Star size={18} className="text-[#C5A059]" /> Metti in Evidenza
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {featureTarget && (
              <div className="bg-zinc-800/50 border border-zinc-700 p-3 flex items-center gap-3">
                <div className="w-12 h-10 bg-zinc-800 overflow-hidden shrink-0">
                  <img src={getListingImage({ images: featureTarget.images, category: featureTarget.category })} alt="" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-sm text-white">{featureTarget.manufacturer} {featureTarget.model}</p>
                  <p className="text-[10px] text-zinc-500">{featureTarget.category?.replace('-',' ')}</p>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Durata</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFeatureDuration('featured_30d')}
                  className={`text-left p-3 border transition-colors ${featureDuration === 'featured_30d' ? 'border-[#C5A059] bg-[#C5A059]/10' : 'border-zinc-700 hover:border-zinc-500'}`}
                  data-testid="feature-option-30d"
                >
                  <p className={`text-xs font-medium mb-0.5 ${featureDuration === 'featured_30d' ? 'text-[#C5A059]' : 'text-white'}`}>30 giorni</p>
                  <p className="text-lg text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>€14,90</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">Una tantum</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFeatureDuration('featured_6m')}
                  className={`text-left p-3 border transition-colors relative ${featureDuration === 'featured_6m' ? 'border-[#C5A059] bg-[#C5A059]/10' : 'border-zinc-700 hover:border-zinc-500'}`}
                  data-testid="feature-option-6m"
                >
                  <span className="absolute -top-2 right-2 text-[9px] font-mono uppercase tracking-wider bg-[#C5A059] text-black px-1.5 py-0.5">Risparmia 33%</span>
                  <p className={`text-xs font-medium mb-0.5 ${featureDuration === 'featured_6m' ? 'text-[#C5A059]' : 'text-white'}`}>6 mesi</p>
                  <p className="text-lg text-white font-medium" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>€59,90</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">≈ €9,98/mese</p>
                </button>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-[#C5A059]/5 border border-[#C5A059]/20">
              <Info size={16} className="text-[#C5A059] shrink-0 mt-0.5" />
              <div className="text-xs text-zinc-300 leading-relaxed">
                <p className="font-medium text-[#C5A059] mb-1">{featureDuration === 'featured_6m' ? 'Annuncio in evidenza per 6 mesi' : 'Annuncio in evidenza per 30 giorni'}</p>
                <p className="text-zinc-400">Il tuo annuncio apparirà con una stella dorata, sarà posizionato tra i primi risultati di ricerca e nella sezione "In Evidenza" della homepage.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setFeatureOpen(false)} className="px-5 py-2.5 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors" data-testid="feature-cancel-btn">
                Annulla
              </button>
              <button onClick={confirmFeature} className="flex items-center gap-2 bg-[#C5A059] text-black px-5 py-2.5 text-sm font-medium hover:bg-[#D4B271] transition-colors" data-testid="feature-confirm-btn">
                <Star size={14} /> Metti in Evidenza
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Listing Dialog */}
      <EditListingDialog
        listing={editListing}
        open={editOpen}
        onClose={() => { setEditOpen(false); setEditListing(null); }}
        onSave={fetchData}
      />
    </div>
  );
}
